import React, { useEffect, useMemo, useState } from 'react';
import cn from 'classnames';

import { beGetSalonTaxesAndFees } from 'actions/salon/beGetSalonTaxesAndFees.action';
import ModalDeleteProduct from 'components/stock/ModalDeleteProduct.component';
import { TaxesAndFeesSection } from 'components/taxes/TaxesAndFeesSection/TaxesAndFeesSection.component';
import TooltipForReceptionist from 'components/TooltipForReceptionist';
import IN from 'constants/in';
import { usePrepareReadOnly } from 'hooks/usePrepare.hook';
import { useTranslation } from 'providers/GILT.provider';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import updateIntercomElement from 'utils/dom/updateIntercomElement.util';

import InputFieldContainer from 'onboarding/common/InputField';

export const EditManufacturerProductSidebar = ({ selectedData, product, isProductLoading, brandList, subBrandList, categoryList, onSave, attemptDelete, onClose, isSaving = false }) => {
  const [saveTitle, setSaveTitle] = useState('');
  const [filterB, setFilterB] = useState(selectedData ? selectedData.brand : '');
  const [filterSub, setFilterSub] = useState(selectedData ? selectedData.sub_brand : '');
  const [filterCat, setFilterCat] = useState(selectedData ? selectedData.category_name : '');
  const [listBrand, setListBrand] = useState(false);
  const [listSubBrand, setListSubBrand] = useState(false);
  const [listCategory, setListCategory] = useState(false);
  const [saveSubBrand, setSaveSubBrand] = useState(false);
  const [saveBrand, setSaveBrand] = useState(false);
  const [saveCategory, setSaveCategory] = useState(false);
  const [deleteProductModal, setDeleteProductModal] = useState(false);
  const [selectedTaxes, setSelectedTaxes] = useState({});
  const [selectedFees, setSelectedFees] = useState({});
  const [isTaxExempt, setIsTaxExempt] = useState();
  const [taxExemptReason, setTaxExemptReason] = useState();

  const { qj: taxesAndFees, isLoading } = usePrepareReadOnly(beGetSalonTaxesAndFees);

  const taxes = useMemo(() => taxesAndFees?.taxes ?? [], [taxesAndFees]);
  const fees = useMemo(() => taxesAndFees?.fees ?? [], [taxesAndFees]);

  const productTaxes = taxes.filter((tax) => tax.applied_to === 'products' || tax.applied_to === 'products_and_services');
  const productFees = fees.filter((fee) => fee.applied_to === 'products' || fee.applied_to === 'products_and_services');

  const editedTaxes = useMemo(() => product?.tax_ids ?? [], [product]);
  const editedFees = useMemo(() => product?.fee_ids ?? [], [product]);

  const defaultTaxes = {
    ...editedTaxes.reduce((acc, tax) => {
      acc[tax] = true;
      return acc;
    }, {}),
  };
  const defaultFees = {
    ...editedFees.reduce((acc, fee) => {
      acc[fee] = true;
      return acc;
    }, {}),
  };

  const { t } = useTranslation();

  useEffect(() => {
    updateIntercomElement({ show: false, tag: 'EditManufacturerProduct.mount()' });

    return () => {
      updateIntercomElement({ show: true, tag: 'EditManufacturerProduct.unmount()' });
    };
  }, []);

  const onSaveProductChanges = () => {
    if (isTaxExempt && !taxExemptReason?.trim()) {
      return;
    }

    const editItem = {
      ...selectedData,
      title: saveTitle || selectedData.title,
      brand: filterB || selectedData.brand,
      sub_brand: filterSub || selectedData.sub_brand,
      category_name: filterCat || selectedData.category_name,
    };

    const selectedTaxesIds = Object.entries(selectedTaxes)
      .filter(([_, value]) => value)
      .map(([key]) => parseInt(key));

    const newTaxes = [...selectedTaxesIds];

    editedTaxes.forEach((tax) => {
      if (selectedTaxes[tax] === undefined) {
        newTaxes.push(tax);
      }
    });
    editItem.taxes = newTaxes;

    const selectedFeesIds = Object.entries(selectedFees)
      .filter(([_, value]) => value)
      .map(([key]) => parseInt(key));
    const newFees = [...selectedFeesIds];
    editedFees.forEach((fee) => {
      if (selectedFees[fee] === undefined) {
        newFees.push(fee);
      }
    });
    editItem.fees = newFees;

    editItem.isTaxExempt = isTaxExempt === undefined ? product?.is_tax_exempt : isTaxExempt;
    editItem.taxExemptReason = taxExemptReason === undefined ? product?.tax_exempt_reason : taxExemptReason;
    onSave(editItem);
  };

  const selectTax = (taxes) => {
    setSelectedTaxes(taxes);
  };

  const selectFee = (fees) => {
    setSelectedFees(fees);
  };

  const toggleExempt = (isExempt) => {
    setIsTaxExempt(isExempt);
  };

  const changeReason = (reason) => {
    setTaxExemptReason(reason);
  };

  const closeDeleteProductModal = () => {
    setDeleteProductModal(false);
  };

  const onDelete = () => {
    if (!isSaving) {
      attemptDelete(selectedData.id);
      closeDeleteProductModal();
      onClose();
    }
  };

  const selectBrand = (item) => {
    setFilterB(item);
    setListBrand(false);
  };

  const searchBrand = (event) => {
    setFilterB(event.target.value);
    setListBrand(true);
  };

  // sub brand
  const selectSubBrand = (item) => {
    setFilterSub(item);
    setListSubBrand(false);
  };

  const searchSubBrand = (event) => {
    setFilterSub(event.target.value);
    setListSubBrand(true);
  };

  const searchCategory = (event) => {
    setFilterCat(event.target.value);
    setListCategory(true);
  };

  const selectCategory = (item) => {
    setFilterCat(item);
    setListCategory(false);
  };

  // brands
  const lowercasedFilter = filterB ? filterB.toLowerCase() : '';

  const predicate = (item) =>
    item
      .trim()
      .normalize('NFD')
      .replace(/^0+/, '')
      .replace(/’/, '')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[\u00E9]/g, '')
      .toLowerCase()
      .includes(lowercasedFilter);
  const filteredData = brandList.filter(predicate);

  // sub brands
  const lowercasedFilterSub = filterSub ? filterSub.toLowerCase() : '';
  const filteredDataSub = subBrandList.filter((item) => item.toLowerCase().includes(lowercasedFilterSub));
  const lowercaseFiltereCat = filterCat ? filterCat.toLowerCase() : '';
  const filteredDataCat = categoryList.filter((item) => item.toLowerCase().includes(lowercaseFiltereCat));
  const btnDisabled = isSaving;

  const buttonClasses = cn({
    'btn btn-violet': true,
    disabled: btnDisabled,
  });

  return (
    <div>
      <div className="product-sidebar">
        <div className="sidebar-header stock">
          {t('stocks.managerLayout.sidebarProductProfile.title')}
          <div role="button" tabIndex={0} className="close-button ico-close" onClick={onClose} />
        </div>
        <div className="sidebar-content product-profile">
          <section key={selectedData.id}>
            <div className="cfix relative InputFieldContainer">
              <InputFieldContainer
                data={{
                  id: `${selectedData.id.toString()}_name`,
                  label: t('stocks.managerLayout.sidebarProductProfile.productName'),
                  type: 'text',
                  placeholder: t('stocks.managerLayout.sidebarProductProfile.productName'),
                  inputValue: `${saveTitle ? saveTitle : selectedData.title}`,
                  inputMode: 'text',
                }}
                onBlurHandler={(value) => {
                  setSaveTitle(value);
                }}
              />
              <TooltipForReceptionist where={IN.stockCb} />
            </div>
            <div className="cfix relative search-filter">
              <label htmlFor="brand-dropdown">{t('stocks.managerLayout.sidebarProductProfile.brand')}</label>
              <input
                value={filterB || ''}
                onBlur={() => {
                  setSaveBrand(false);
                }}
                onClick={() => {
                  setListSubBrand(false);
                }}
                onChange={searchBrand}
                type="text"
                className="input-default"
                placeholder={t('stocks.managerLayout.sidebarProductProfile.searchBrandsPlaceholder')}
              />
              {listBrand === true && filterB.length > 0 && (
                <div className="dropdown-suggestions">
                  {filteredData.length === 0 && !saveBrand && (
                    <div className="btn-add-new-brand" key="btn-add-key" role="button" tabIndex="0" onClick={() => setSaveBrand(true)}>
                      {t('stocks.managerLayout.sidebarProductProfile.asNewBrandButton')}
                    </div>
                  )}
                  {filteredData.map((item) => (
                    <div className="item" key={item} onClick={() => selectBrand(item)} role="menuitem" tabIndex="0">
                      <div>{item}</div>
                    </div>
                  ))}
                </div>
              )}
              <TooltipForReceptionist where={IN.stockCb} />
            </div>
            <div className="cfix relative search-filter">
              <label htmlFor="category">{t('stocks.managerLayout.sidebarProductProfile.subBrand')}</label>

              <input
                value={filterSub || ''}
                onBlur={() => setSaveSubBrand(false)}
                onClick={() => {
                  setListBrand(false);
                }}
                onChange={searchSubBrand}
                type="text"
                className="input-default"
                placeholder={t('stocks.managerLayout.sidebarProductProfile.subBrandPlaceholder')}
              />
              {listSubBrand === true && filterSub.length > 0 && (
                <div className="dropdown-suggestions">
                  {filteredDataSub.length === 0 && !saveSubBrand && (
                    <div className="btn-add-new-brand" key="btn-add-key" role="button" tabIndex="0" onClick={() => setSaveSubBrand(true)}>
                      {t('stocks.managerLayout.sidebarProductProfile.asNewSubBrandButton')}
                    </div>
                  )}
                  {filteredDataSub.map((item) => (
                    <div className="item" key={item} onClick={() => selectSubBrand(item)} role="menuitem" tabIndex="0">
                      <div>{item}</div>
                    </div>
                  ))}
                </div>
              )}
              <TooltipForReceptionist where={IN.stockCb} />
            </div>
            <div className="cfix relative search-filter">
              <label htmlFor="category">{t('stocks.managerLayout.sidebarProductProfile.category')}</label>

              <input
                value={filterCat || ''}
                onBlur={() => setSaveCategory(false)}
                onClick={() => {
                  setListBrand(false);
                }}
                onChange={searchCategory}
                type="text"
                className="input-default"
                placeholder={t('stocks.managerLayout.sidebarProductProfile.categoryPlaceholder')}
              />
              {listCategory === true && filterCat.length > 0 && (
                <div className="dropdown-suggestions">
                  {filteredDataCat.length === 0 && !saveCategory && (
                    <div className="btn-add-new-brand" key="btn-add-key" role="button" tabIndex="0" onClick={() => setSaveCategory(true)}>
                      {t('stocks.managerLayout.sidebarProductProfile.asNewCategoryButton')}
                    </div>
                  )}
                  {filteredDataCat.map((item) => (
                    <div className="item" key={item} onClick={() => selectCategory(item)} role="menuitem" tabIndex="0">
                      <div>{item}</div>
                    </div>
                  ))}
                </div>
              )}
              <TooltipForReceptionist where={IN.stockCb} />
            </div>
            <div className="width-half">
              <InputFieldContainer
                data={{
                  id: `${selectedData.id.toString()}_sku`,
                  label: t('stocks.managerLayout.sidebarProductProfile.sku'),
                  type: 'text',
                  placeholder: t('stocks.managerLayout.sidebarProductProfile.sku'),
                  inputValue: `${selectedData.barcode || ''}`,
                  inputMode: 'text',
                }}
                readOnly
              />
            </div>
            <TaxesAndFeesSection
              isEdit
              location="product"
              taxes={productTaxes}
              fees={productFees}
              isLoading={isLoading || isProductLoading}
              onSelectTax={selectTax}
              onSelectFee={selectFee}
              onToggleExempt={toggleExempt}
              onChangeReason={changeReason}
              taxesByDefault={defaultTaxes}
              feesByDefault={defaultFees}
              isExemptByDefault={product?.is_tax_exempt ?? false}
              reasonByDefault={product?.tax_exempt_reason ?? ''}
            />
          </section>
        </div>
        <footer className="product-sidebar-footer product-profile">
          <div className="cfix relative">
            <button
              className="btn btn-violet-link"
              role="menuitem"
              tabIndex={0}
              onClick={() => {
                setDeleteProductModal(true);
              }}
            >
              {t('common.buttons.deleteUpp')}
            </button>
            <TooltipForReceptionist where={IN.stockFooter} />
          </div>
          <div className="cfix relative">
            <button onClick={() => void onSaveProductChanges()} disabled={btnDisabled} className={buttonClasses}>
              {t('common.buttons.save')}
            </button>
            <TooltipForReceptionist where={IN.stockFooter} />
          </div>
        </footer>
      </div>
      {fromAnyToBool(deleteProductModal) && (
        <ModalDeleteProduct
          icon="alert"
          title={t('stocks.managerLayout.deleteProductModal.title1')}
          message={t('stocks.managerLayout.deleteProductModal.description1')}
          onClose={closeDeleteProductModal}
          onConfirm={onDelete}
        />
      )}
    </div>
  );
};
